import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  Loader2,
  Send,
  Star,
  ThumbsUp,
} from 'lucide-react';
import {
  appointmentService,
  doctorService,
  getLocalizedValue,
  normalizeImageValue,
  referenceService,
} from '@/services/api';

const STATUS_BY_ID = {
  1: 'pending',
  2: 'accepted',
  3: 'in_clinic',
  4: 'done',
  5: 'cancel',
  6: 'cancel',
  7: 'pending',
};

const normalizeAppointmentState = (state, statusId) => {
  const raw = String(state || STATUS_BY_ID[statusId] || 'pending').trim().toLowerCase();

  if (['accepted', 'confirmed', 'مقبول'].includes(raw)) return 'accepted';
  if (['pending', 'draft', 'request', 'waiting', 'قيد الانتظار'].includes(raw)) return 'pending';
  if (['in_clinic', 'in_progress', 'داخل العيادة'].includes(raw)) return 'in_clinic';
  if (['done', 'completed', 'منتهي', 'مكتمل'].includes(raw)) return 'done';
  if (['cancel', 'cancelled', 'canceled', 'rejected', 'no_show', 'ملغي', 'ملغى'].includes(raw)) return 'cancel';
  if (['unfinished', 'غير مكتمل'].includes(raw)) return 'unfinished';

  return raw;
};

const readBoolean = (value, fallback = false) => {
  if (value == null) return fallback;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;

  const normalized = String(value).trim().toLowerCase();
  if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
  if (['false', '0', 'no', 'n', ''].includes(normalized)) return false;
  return fallback;
};

const readNumber = (value) => {
  if (value == null || value === false || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const readObject = (value) => (
  value && typeof value === 'object' && !Array.isArray(value) ? value : {}
);

const extractItems = (value) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== 'object') return [];

  for (const key of ['data', 'items', 'records', 'results', 'reviews']) {
    const nested = extractItems(value[key]);
    if (nested.length > 0 || Array.isArray(value[key])) {
      return nested;
    }
  }

  return [];
};

const getAppointmentReviewState = (appointment) => {
  const source = readObject(appointment);
  const doctorReview = readObject(
    source.doctor_review ||
    source.doctorReview ||
    source.review ||
    source.doctor_review_data ||
    source.review_data
  );
  const doctorReviewRows = Array.isArray(source.doctor_reviews)
    ? source.doctor_reviews
    : Array.isArray(source.reviews)
      ? source.reviews
      : [];
  const canReviewDoctor = readBoolean(source.can_review_doctor, true);
  const hasDoctorReview =
    readBoolean(
      source.doctor_reviewed ??
      source.doctor_review_submitted ??
      source.is_doctor_reviewed ??
      source.has_doctor_review ??
      source.has_review ??
      source.reviewed ??
      source.already_reviewed,
      false
    ) ||
    readNumber(source.doctor_review_id) != null ||
    readNumber(source.review_id) != null ||
    Object.keys(doctorReview).length > 0 ||
    doctorReviewRows.length > 0 ||
    canReviewDoctor === false;

  return {
    isCompleted:
      normalizeAppointmentState(
        source.state || source.status,
        source.appointment_status_id
      ) === 'done',
    alreadyReviewed: hasDoctorReview,
    canReviewDoctor,
  };
};

const getErrorMessage = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  '';

const RateDoctorPage = () => {
  const { doctorId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const lang = i18n.language || 'ar';
  const appointmentId = searchParams.get('appointment');
  const stateDoctor = location.state?.doctor || null;
  const stateAppointment = location.state?.appointment || null;

  const [doctor, setDoctor] = useState(stateDoctor);
  const [loadingDoctor, setLoadingDoctor] = useState(!stateDoctor);
  const [loadingAppointment, setLoadingAppointment] = useState(!!appointmentId && !stateAppointment);
  const [appointmentGate, setAppointmentGate] = useState(() =>
    stateAppointment ? getAppointmentReviewState(stateAppointment) : null
  );
  const [appointmentError, setAppointmentError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [selectedReviewId, setSelectedReviewId] = useState(null);
  const [reviewOptions, setReviewOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [optionsError, setOptionsError] = useState('');

  useEffect(() => {
    let active = true;

    if (!doctorId) {
      setLoadingDoctor(false);
      return () => {
        active = false;
      };
    }

    setLoadingDoctor(!stateDoctor);

    doctorService
      .getById(doctorId)
      .then((response) => {
        if (!active) return;
        setDoctor(response?.data?.data || response?.data || stateDoctor);
      })
      .catch((error) => {
        console.error('Failed to fetch doctor:', error);
        if (!active || stateDoctor) return;
        setDoctor(null);
      })
      .finally(() => {
        if (active) {
          setLoadingDoctor(false);
        }
      });

    return () => {
      active = false;
    };
  }, [doctorId, stateDoctor]);

  useEffect(() => {
    let active = true;

    if (!appointmentId) {
      setAppointmentGate(null);
      setAppointmentError('');
      setLoadingAppointment(false);
      return () => {
        active = false;
      };
    }

    if (stateAppointment) {
      setAppointmentGate(getAppointmentReviewState(stateAppointment));
      setAppointmentError('');
    }

    setLoadingAppointment(!stateAppointment);
    appointmentService
      .getById(appointmentId)
      .then((response) => {
        if (!active) return;
        setAppointmentGate(getAppointmentReviewState(response?.data?.data));
        setAppointmentError('');
      })
      .catch((error) => {
        console.error('Failed to fetch appointment before review:', error);
        if (!active) return;
        setAppointmentGate(null);
        setAppointmentError(getErrorMessage(error));
      })
      .finally(() => {
        if (active) {
          setLoadingAppointment(false);
        }
      });

    return () => {
      active = false;
    };
  }, [appointmentId, stateAppointment]);

  const handleRatingChange = useCallback((value) => {
    setRating(value);
    setSelectedReviewId(null);
    setReviewOptions([]);
    setOptionsError('');
  }, []);

  useEffect(() => {
    let active = true;

    if (rating <= 0) {
      setReviewOptions([]);
      setLoadingOptions(false);
      setOptionsError('');
      return () => {
        active = false;
      };
    }

    setLoadingOptions(true);
    setOptionsError('');

    referenceService
      .getPredefinedReviews({ rating })
      .then((response) => {
        if (!active) return;
        const options = extractItems(response?.data)
          .map((item) => {
            const source = readObject(item);
            const id = readNumber(source.id);
            const text =
              getLocalizedValue(source.text, lang) ||
              getLocalizedValue(source.review, lang) ||
              getLocalizedValue(source.name, lang);

            return id && text ? { id, text } : null;
          })
          .filter(Boolean);
        setReviewOptions(options);
      })
      .catch((error) => {
        console.error('Failed to load predefined reviews:', error);
        if (!active) return;
        setReviewOptions([]);
        setOptionsError(getErrorMessage(error));
      })
      .finally(() => {
        if (active) {
          setLoadingOptions(false);
        }
      });

    return () => {
      active = false;
    };
  }, [rating, lang]);

  const reviewUnavailableReason = useMemo(() => {
    if (!appointmentId) {
      return lang === 'ar'
        ? 'التقييم متاح فقط من تفاصيل موعد منتهي.'
        : 'Reviews are available only from a completed appointment.';
    }
    if (appointmentError) {
      return appointmentError;
    }
    if (!appointmentGate) {
      return lang === 'ar'
        ? 'تعذر التحقق من حالة الموعد.'
        : 'Could not verify appointment status.';
    }
    if (appointmentGate.alreadyReviewed) {
      return lang === 'ar'
        ? 'تم إرسال تقييم لهذا الموعد مسبقاً.'
        : 'A review was already submitted for this appointment.';
    }
    if (!appointmentGate.isCompleted) {
      return lang === 'ar'
        ? 'يمكن تقييم الخدمة بعد انتهاء الموعد فقط.'
        : 'You can review the service only after the appointment is completed.';
    }
    if (appointmentGate.canReviewDoctor === false) {
      return lang === 'ar'
        ? 'التقييم غير متاح لهذا الموعد.'
        : 'Review is not available for this appointment.';
    }
    return '';
  }, [appointmentError, appointmentGate, appointmentId, lang]);

  const canSubmitReview = !!appointmentId && !reviewUnavailableReason;

  const doctorName = useMemo(() => {
    if (!doctor) return '';
    const resolved = lang === 'ar'
      ? doctor.name_ar || getLocalizedValue(doctor.name, lang) || doctor.name
      : doctor.name_en || getLocalizedValue(doctor.name, lang) || doctor.name;
    return typeof resolved === 'string' ? resolved : '';
  }, [doctor, lang]);

  const doctorSpecialty = useMemo(() => {
    const specialty =
      doctor?.specialty ||
      doctor?.speciality ||
      doctor?.specialties?.[0] ||
      doctor?.specialities?.[0] ||
      {};

    const resolved = lang === 'ar'
      ? specialty.name_ar || getLocalizedValue(specialty.name, lang)
      : specialty.name_en || getLocalizedValue(specialty.name, lang);
    return typeof resolved === 'string' ? resolved : '';
  }, [doctor, lang]);

  const doctorImage = useMemo(
    () =>
      normalizeImageValue(
        doctor?.image ||
          doctor?.image_url ||
          doctor?.doctor_image ||
          doctor?.media?.[0]?.url ||
          doctor?.media?.[0]?.thumb ||
          ''
      ),
    [doctor]
  );

  const handleSubmit = async () => {
    if (!isAuthenticated) {
      toast.error(lang === 'ar' ? 'يرجى تسجيل الدخول أولاً' : 'Please login first');
      navigate('/login', {
        state: { from: `${location.pathname}${location.search}` },
      });
      return;
    }

    if (!canSubmitReview) {
      toast.error(reviewUnavailableReason || (lang === 'ar' ? 'التقييم غير متاح' : 'Review is not available'));
      return;
    }

    if (rating === 0) {
      toast.error(lang === 'ar' ? 'يرجى اختيار تقييم' : 'Please select a rating');
      return;
    }

    setSubmitting(true);
    try {
      await doctorService.addReview({
        appointment_id: readNumber(appointmentId) ?? appointmentId,
        doctor_id: readNumber(doctorId) ?? doctorId,
        rating,
        predefined_review_ids: selectedReviewId ? [selectedReviewId] : [],
      });

      setAppointmentGate((current) => ({
        ...(current || {}),
        alreadyReviewed: true,
        canReviewDoctor: false,
      }));
      setSubmitted(true);
      toast.success(
        lang === 'ar' ? 'شكراً لتقييمك!' : 'Thank you for your review!',
        { description: lang === 'ar' ? 'تقييمك يساعد الآخرين' : 'Your feedback helps others' }
      );
    } catch (error) {
      console.error('Failed to submit review:', error);
      toast.error(
        lang === 'ar' ? 'فشل في إرسال التقييم' : 'Failed to submit review',
        { description: getErrorMessage(error) || (lang === 'ar' ? 'يرجى المحاولة مرة أخرى' : 'Please try again') }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const getRatingText = (value) => {
    const texts = {
      1: { en: 'Poor', ar: 'سيء' },
      2: { en: 'Fair', ar: 'مقبول' },
      3: { en: 'Good', ar: 'جيد' },
      4: { en: 'Very Good', ar: 'جيد جداً' },
      5: { en: 'Excellent', ar: 'ممتاز' },
    };
    return texts[value] ? (lang === 'ar' ? texts[value].ar : texts[value].en) : '';
  };

  if (loadingDoctor || loadingAppointment) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-900">
        <Loader2 className="h-8 w-8 animate-spin text-[#6270c8]" />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 dark:bg-slate-900" data-testid="rate-success">
        <Card className="w-full max-w-md border-0 text-center shadow-lg dark:bg-slate-800">
          <CardContent className="pb-8 pt-12">
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
              <ThumbsUp className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="mb-2 text-2xl font-bold text-slate-900 dark:text-white">
              {lang === 'ar' ? 'شكراً لك!' : 'Thank You!'}
            </h2>
            <p className="mb-6 text-slate-600 dark:text-slate-300">
              {lang === 'ar'
                ? 'تم حفظ تقييمك لهذا الموعد.'
                : 'Your review has been saved for this appointment.'}
            </p>
            <div className="flex justify-center gap-3">
              <Button variant="outline" onClick={() => navigate(appointmentId ? `/appointments/${appointmentId}` : '/appointments')}>
                {lang === 'ar' ? 'تفاصيل الموعد' : 'Appointment Details'}
              </Button>
              <Button className="bg-[#6270c8] hover:bg-[#5360b5]" onClick={() => navigate('/appointments')}>
                {lang === 'ar' ? 'مواعيدي' : 'My Appointments'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!doctor || reviewUnavailableReason) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 dark:bg-slate-900">
        <Card className="w-full max-w-md border-0 shadow-sm dark:bg-slate-800">
          <CardContent className="p-6 text-center">
            <AlertCircle className="mx-auto mb-4 h-12 w-12 text-slate-500 dark:text-slate-300" />
            <p className="mb-4 text-slate-600 dark:text-slate-300">
              {!doctor
                ? lang === 'ar'
                  ? 'تعذر تحميل بيانات الطبيب'
                  : 'Doctor data could not be loaded'
                : reviewUnavailableReason}
            </p>
            <Button variant="outline" onClick={() => navigate(appointmentId ? `/appointments/${appointmentId}` : -1)}>
              <ChevronLeft className="me-2 h-4 w-4" />
              {t('common.back')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="rate-doctor-page">
      <div className="border-b bg-white dark:border-slate-700 dark:bg-slate-800">
        <div className="container mx-auto px-4 py-6">
          <Button
            variant="ghost"
            size="sm"
            className="mb-4 -ms-2"
            onClick={() => navigate(appointmentId ? `/appointments/${appointmentId}` : -1)}
          >
            <ChevronLeft className="me-1 h-4 w-4" />
            {t('common.back')}
          </Button>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            {lang === 'ar' ? 'تقييم الخدمة' : 'Leave a Review'}
          </h1>
        </div>
      </div>

      <div className="container mx-auto max-w-2xl px-4 py-8">
        <Card className="border-0 shadow-sm dark:bg-slate-800">
          <CardHeader className="pb-2 text-center">
            <Avatar className="mx-auto mb-4 h-24 w-24 rounded-xl">
              <AvatarImage src={doctorImage} alt={doctorName} className="object-cover" />
              <AvatarFallback className="rounded-xl bg-[#6270c8] text-2xl text-white">
                {doctorName?.charAt(0) || 'D'}
              </AvatarFallback>
            </Avatar>
            <CardTitle>{doctorName}</CardTitle>
            {doctorSpecialty && <CardDescription>{doctorSpecialty}</CardDescription>}
          </CardHeader>

          <CardContent className="space-y-6">
            <div className="text-center">
              <p className="mb-3 text-sm text-slate-600 dark:text-slate-300">
                {lang === 'ar' ? 'كيف كانت تجربتك مع هذا الطبيب؟' : 'How was your experience with this doctor?'}
              </p>
              <div className="flex justify-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => handleRatingChange(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="rounded-full p-1 transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-[#6270c8]/40"
                    data-testid={`star-${star}`}
                  >
                    <Star
                      className={`h-10 w-10 transition-colors ${
                        star <= (hoverRating || rating)
                          ? 'fill-amber-400 text-amber-400'
                          : 'text-slate-300 dark:text-slate-600'
                      }`}
                    />
                  </button>
                ))}
              </div>
              {(rating > 0 || hoverRating > 0) && (
                <p className="mt-2 text-lg font-medium text-[#6270c8]">
                  {getRatingText(hoverRating || rating)}
                </p>
              )}
            </div>

            <div className="rounded-2xl bg-slate-50 p-4 dark:bg-white/[0.04]">
              <p className="mb-3 text-sm font-bold text-slate-800 dark:text-slate-100">
                {lang === 'ar' ? 'اختر تقييماً' : 'Choose a review'}
              </p>
              {rating <= 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {lang === 'ar'
                    ? 'اختر عدد النجوم لعرض اقتراحات مناسبة'
                    : 'Select stars to see matching review suggestions'}
                </p>
              ) : loadingOptions ? (
                <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {lang === 'ar' ? 'جاري تحميل الاقتراحات...' : 'Loading suggestions...'}
                </div>
              ) : optionsError ? (
                <p className="text-sm text-rose-600 dark:text-rose-300">
                  {optionsError}
                </p>
              ) : reviewOptions.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {lang === 'ar' ? 'لا توجد خيارات محفوظة' : 'No saved options found'}
                </p>
              ) : (
                <div className="space-y-2">
                  {reviewOptions.map((option) => {
                    const selected = selectedReviewId === option.id;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => setSelectedReviewId(selected ? null : option.id)}
                        className={`flex w-full items-center justify-between gap-3 rounded-xl border p-3 text-start transition-colors ${
                          selected
                            ? 'border-[#6270c8] bg-[#6270c8]/10 text-slate-900 dark:text-white'
                            : 'border-slate-200 bg-white text-slate-700 hover:border-[#6270c8]/50 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200'
                        }`}
                      >
                        <span className="font-semibold">{option.text}</span>
                        <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md border ${
                          selected
                            ? 'border-[#6270c8] text-[#6270c8]'
                            : 'border-slate-300 text-transparent dark:border-slate-600'
                        }`}>
                          <CheckCircle2 className="h-4 w-4" />
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <Button
              className="w-full bg-[#6270c8] hover:bg-[#5360b5]"
              size="lg"
              onClick={handleSubmit}
              disabled={submitting || rating === 0 || !canSubmitReview}
              data-testid="submit-review-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="me-2 h-4 w-4 animate-spin" />
                  {t('common.loading')}
                </>
              ) : (
                <>
                  <Send className="me-2 h-4 w-4" />
                  {lang === 'ar' ? 'إرسال التقييم' : 'Submit Review'}
                </>
              )}
            </Button>

            <p className="text-center text-xs text-slate-400">
              {lang === 'ar'
                ? 'يمكن إرسال تقييم واحد فقط لكل موعد.'
                : 'Only one review can be submitted for each appointment.'}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RateDoctorPage;
